import { ReferenceDirection, ViewIds, type ArdoqId } from '@ardoq/api-types';
import {
  FieldSource,
  type BubbleChartSources,
  type BubbleChartValueSource,
  type BubbleChartViewSettings,
  type ComponentTypeSummary,
  type FieldPickerArgs,
  type ReferenceTypeSummary,
} from '@ardoq/bubble-chart';
import { WILDCARD } from '@ardoq/common-helpers';
import {
  DropdownOptionType,
  type DropdownItem,
  type DropdownOption,
} from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { t, type I18nLocale } from '@ardoq/i18n';
import { IconName } from '@ardoq/icons';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { colors } from '@ardoq/design-tokens';
import { Box } from '@ardoq/layout';
import { dispatchAction } from '@ardoq/rxbeach';
import { Text } from '@ardoq/typography';
import {
  SettingsType,
  updateViewSettings,
  type DropdownConfig,
  type SettingsConfig,
  type ToggleConfig,
} from '@ardoq/view-settings';
import {
  getLayoutDropdownConfig,
  showBubblesWithZeroValueConfig,
} from '../viewSettings/viewSettingsUtils';

const DROPDOWN_MAX_HEIGHT = 400;

const CLEARED_AXIS_FIELDS = {
  selectedFieldNameX: '',
  selectedFieldNameY: '',
  selectedFieldNameRadius: '',
  labelFilter: undefined,
} satisfies Partial<BubbleChartViewSettings>;

const toSourceOfBubblesSettings = (
  selectedComponentTypes: ArdoqId[]
): Partial<BubbleChartViewSettings> => ({
  ...CLEARED_AXIS_FIELDS,
  selectedComponentTypes,
  fieldSource: FieldSource.BY_COMPONENT,
  selectedReferenceTypeName: null,
  valueSourceComponentTypes: null,
});

const toComponentValueSourceSettings = (
  valueSourceComponentTypes: ArdoqId[]
): Partial<BubbleChartViewSettings> => ({
  ...CLEARED_AXIS_FIELDS,
  fieldSource: FieldSource.BY_COMPONENT,
  selectedReferenceTypeName: null,
  valueSourceComponentTypes,
});

const toReferenceValueSourceSettings = ({
  direction,
  referenceTypeName,
}: ReferenceTypeSummary): Partial<BubbleChartViewSettings> => ({
  ...CLEARED_AXIS_FIELDS,
  fieldSource: FieldSource.BY_REFERENCE,
  selectedReferenceDirection: direction,
  selectedReferenceTypeName: referenceTypeName,
  valueSourceComponentTypes: null,
});

const toSelectHandler =
  (
    isActive: boolean,
    settings: Partial<BubbleChartViewSettings>
  ): VoidFunction =>
  () => {
    if (isActive) {
      return;
    }

    dispatchAction(
      updateViewSettings({
        viewId: ViewIds.BUBBLE,
        settings,
        persistent: true,
      })
    );
  };

const toComponentCountLabel = (
  count: number,
  i18nLocale: I18nLocale
): string =>
  count === 1
    ? t(
        i18nLocale,
        'tabview:bubbleChart.componentCount_one',
        { count },
        '{{count}} component'
      )
    : t(
        i18nLocale,
        'tabview:bubbleChart.componentCount_other',
        { count },
        '{{count}} components'
      );

const toFieldCountLabel = (count: number, i18nLocale: I18nLocale): string => {
  if (count === 0) {
    return t(
      i18nLocale,
      'tabview:bubbleChart.noNumericFields',
      'No numeric fields'
    );
  }

  return count === 1
    ? t(
        i18nLocale,
        'tabview:bubbleChart.fieldCount_one',
        { count },
        '{{count}} field'
      )
    : t(
        i18nLocale,
        'tabview:bubbleChart.fieldCount_other',
        { count },
        '{{count}} fields'
      );
};

type OptionLeading = Pick<DropdownOption, 'leftIcon' | 'leftContent'>;

const ALL_OPTION_LEADING: OptionLeading = {
  leftIcon: { name: IconName.CATEGORY },
};

const toComponentTypeLeading = ({
  representationData,
}: ComponentTypeSummary): OptionLeading => ({
  leftContent: (
    <ComponentRepresentation {...representationData} color={colors.icon} />
  ),
});

const toReferenceLeading = ({
  direction,
}: ReferenceTypeSummary): OptionLeading => ({
  leftIcon: {
    name:
      direction === ReferenceDirection.INCOMING
        ? IconName.ARROW_BACK
        : IconName.ARROW_FORWARD,
  },
});

type ToSummaryOptionArgs = {
  label: string;
  leading: OptionLeading;
  description?: string;
  numericFieldCount: number;
  isActive: boolean;
  settings: Partial<BubbleChartViewSettings>;
  i18nLocale: I18nLocale;
};

const toSummaryOption = ({
  label,
  leading,
  description,
  numericFieldCount,
  isActive,
  settings,
  i18nLocale,
}: ToSummaryOptionArgs): DropdownOption => ({
  type: DropdownOptionType.OPTION,
  name: label,
  label,
  ...leading,
  description,
  isSelected: isActive,
  rightLabel: toFieldCountLabel(numericFieldCount, i18nLocale),
  onClick: toSelectHandler(isActive, settings),
});

const toSectionDescription = (description: string): DropdownItem => ({
  type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
  content: (
    <Box key={description} paddingX="small" paddingBottom="xsmall">
      <Text variant="caption" color="textSubtle">
        {description}
      </Text>
    </Box>
  ),
});

const hasAnyComponentTypeId = (
  { ids }: ComponentTypeSummary,
  componentTypeIds: ArdoqId[]
): boolean => ids.some(id => componentTypeIds.includes(id));

type SourceControlsArgs = {
  sources: BubbleChartSources;
  selectedComponentTypes: ArdoqId[];
  i18nLocale: I18nLocale;
};

const toSourceOfBubblesConfig = ({
  sources: {
    options: { allComponentsSummary, componentTypeSummaries },
  },
  selectedComponentTypes,
  i18nLocale,
}: SourceControlsArgs): DropdownConfig => {
  const isAllSelected = selectedComponentTypes.includes(WILDCARD);

  return {
    id: 'selectedComponentTypes',
    type: SettingsType.DROPDOWN,
    label: t(
      i18nLocale,
      'tabview:bubbleChart.sourceOfBubbles',
      'Source of bubbles'
    ),
    iconName: IconName.ADD_BUBBLE,
    options: [
      toSummaryOption({
        label: t(i18nLocale, 'bubbleChart:componentTypeSelect.all', 'All'),
        leading: ALL_OPTION_LEADING,
        description: toComponentCountLabel(
          allComponentsSummary.componentCount,
          i18nLocale
        ),
        numericFieldCount: allComponentsSummary.numericFieldCount,
        isActive: isAllSelected,
        settings: toSourceOfBubblesSettings([WILDCARD]),
        i18nLocale,
      }),
      { type: DropdownOptionType.DIVIDER },
      ...componentTypeSummaries.map(summary => {
        const isActive =
          !isAllSelected &&
          hasAnyComponentTypeId(summary, selectedComponentTypes);

        return toSummaryOption({
          label: summary.name,
          leading: toComponentTypeLeading(summary),
          description: toComponentCountLabel(
            summary.componentCount,
            i18nLocale
          ),
          numericFieldCount: summary.numericFieldCount,
          isActive,
          settings: toSourceOfBubblesSettings(summary.ids),
          i18nLocale,
        });
      }),
    ],
    dropdownSize: DropdownSize.M,
    maxDropdownHeight: DROPDOWN_MAX_HEIGHT,
  };
};

const isComponentValueSourceOf = (
  valueSource: BubbleChartValueSource,
  summary: ComponentTypeSummary,
  isOnlyBubbleType: boolean
): boolean => {
  if (valueSource.type !== 'component') {
    return false;
  }

  if (valueSource.componentTypeIds.includes(WILDCARD)) {
    return isOnlyBubbleType;
  }

  return hasAnyComponentTypeId(summary, valueSource.componentTypeIds);
};

const isReferenceValueSourceOf = (
  valueSource: BubbleChartValueSource,
  { direction, referenceTypeName }: ReferenceTypeSummary
): boolean =>
  valueSource.type === 'reference' &&
  valueSource.direction === direction &&
  valueSource.referenceTypeName === referenceTypeName;

const toReferenceDescription = (
  { direction, linkedComponentTypeNames }: ReferenceTypeSummary,
  i18nLocale: I18nLocale
): string => {
  const componentTypes = linkedComponentTypeNames.join(', ');

  return direction === ReferenceDirection.INCOMING
    ? t(
        i18nLocale,
        'tabview:bubbleChart.incomingFrom',
        { componentTypes },
        'Incoming from {{componentTypes}}'
      )
    : t(
        i18nLocale,
        'tabview:bubbleChart.outgoingTo',
        { componentTypes },
        'Outgoing to {{componentTypes}}'
      );
};

const toComponentValueSourceOptions = ({
  sources: {
    valueSource,
    options: { allComponentsSummary, componentTypeSummaries },
  },
  selectedComponentTypes,
  i18nLocale,
}: SourceControlsArgs): DropdownItem[] => {
  const isAllSelected = selectedComponentTypes.includes(WILDCARD);
  const bubbleTypeSummaries = isAllSelected
    ? componentTypeSummaries
    : componentTypeSummaries.filter(summary =>
        hasAnyComponentTypeId(summary, selectedComponentTypes)
      );

  const allOption = isAllSelected
    ? [
        toSummaryOption({
          label: t(i18nLocale, 'bubbleChart:componentTypeSelect.all', 'All'),
          leading: ALL_OPTION_LEADING,
          numericFieldCount: allComponentsSummary.numericFieldCount,
          isActive:
            valueSource.type === 'component' &&
            valueSource.componentTypeIds.includes(WILDCARD),
          settings: toComponentValueSourceSettings([WILDCARD]),
          i18nLocale,
        }),
      ]
    : [];

  return [
    {
      type: DropdownOptionType.HEADER,
      label: t(
        i18nLocale,
        'tabview:bubbleChart.fromComponent',
        'From component'
      ),
    },
    toSectionDescription(
      t(
        i18nLocale,
        'tabview:bubbleChart.fromComponentDescription',
        'Fields on the component itself'
      )
    ),
    ...allOption,
    ...bubbleTypeSummaries.map(summary => {
      const isActive = isComponentValueSourceOf(
        valueSource,
        summary,
        !isAllSelected && bubbleTypeSummaries.length === 1
      );

      return toSummaryOption({
        label: summary.name,
        leading: toComponentTypeLeading(summary),
        numericFieldCount: summary.numericFieldCount,
        isActive,
        settings: toComponentValueSourceSettings(summary.ids),
        i18nLocale,
      });
    }),
  ];
};

const toReferenceValueSourceOptions = ({
  sources: {
    valueSource,
    options: { referenceTypeSummaries },
  },
  i18nLocale,
}: SourceControlsArgs): DropdownItem[] => {
  if (referenceTypeSummaries.length === 0) {
    return [];
  }

  return [
    { type: DropdownOptionType.DIVIDER },
    {
      type: DropdownOptionType.HEADER,
      label: t(i18nLocale, 'tabview:bubbleChart.viaReference', 'Via reference'),
    },
    toSectionDescription(
      t(
        i18nLocale,
        'tabview:bubbleChart.viaReferenceDescription',
        'Pick one reference in your dataset. Fields come from the reference and the component it links to.'
      )
    ),
    ...referenceTypeSummaries.map(summary => {
      const isActive = isReferenceValueSourceOf(valueSource, summary);

      return toSummaryOption({
        label: summary.referenceTypeName,
        leading: toReferenceLeading(summary),
        description: toReferenceDescription(summary, i18nLocale),
        numericFieldCount: summary.numericFieldCount,
        isActive,
        settings: toReferenceValueSourceSettings(summary),
        i18nLocale,
      });
    }),
  ];
};

const toValueSourceConfig = (args: SourceControlsArgs): DropdownConfig => ({
  id: 'valueSource',
  type: SettingsType.DROPDOWN,
  label: t(
    args.i18nLocale,
    'tabview:bubbleChart.sourceOfValues',
    'Source of values'
  ),
  iconName: IconName.ADD_CHART,
  options: [
    ...toComponentValueSourceOptions(args),
    ...toReferenceValueSourceOptions(args),
  ],
  dropdownSize: DropdownSize.M,
  maxDropdownHeight: DROPDOWN_MAX_HEIGHT,
});

type FieldPicker = (args: FieldPickerArgs) => DropdownConfig;

const toAxisFieldPicker =
  (fieldPicker: FieldPicker, hasValueSource: boolean): FieldPicker =>
  args => {
    const config = fieldPicker({
      ...args,
      dropdownSize: DropdownSize.M,
      maxDropdownHeight: DROPDOWN_MAX_HEIGHT,
    });

    if (hasValueSource) {
      return config;
    }

    return { ...config, isDisabled: true, popoverText: undefined };
  };

/**
 * `ViewSettingsRow` forwards a click anywhere on a toggle row to its checkbox,
 * even when the checkbox is disabled, so a disabled toggle carries no update
 * handler.
 */
const toShowZeroSizeBubblesConfig = (
  viewSettings: BubbleChartViewSettings,
  hasValueSource: boolean,
  i18nLocale: I18nLocale
): ToggleConfig => {
  const config: ToggleConfig = {
    ...showBubblesWithZeroValueConfig(viewSettings, i18nLocale),
    popoverText: t(
      i18nLocale,
      'tabview:bubbleChart.showSizeZeroBubblesInfo',
      'Show bubbles even when they have no measurable size.'
    ),
  };

  if (hasValueSource) {
    return { ...config, isDisabled: false };
  }

  return { ...config, isDisabled: true, onViewSettingsUpdate: undefined };
};

type ToViewModifiersWithSourcesArgs = SourceControlsArgs & {
  viewSettings: BubbleChartViewSettings;
  fieldPicker: FieldPicker;
};

export const toViewModifiersWithSources = ({
  sources,
  selectedComponentTypes,
  viewSettings,
  fieldPicker,
  i18nLocale,
}: ToViewModifiersWithSourcesArgs): SettingsConfig[] => {
  const hasValueSource = sources.valueSource.type !== 'none';
  const axisFieldPicker = toAxisFieldPicker(fieldPicker, hasValueSource);

  return [
    toSourceOfBubblesConfig({
      sources,
      selectedComponentTypes,
      i18nLocale,
    }),
    toValueSourceConfig({
      sources,
      selectedComponentTypes,
      i18nLocale,
    }),
    axisFieldPicker({
      fieldName: 'selectedFieldNameX',
      label: t(i18nLocale, 'tabview:bubbleChart.xAxis', 'X-axis'),
      iconName: IconName.ARROW_RIGHT_ALT,
    }),
    axisFieldPicker({
      fieldName: 'selectedFieldNameY',
      label: t(i18nLocale, 'tabview:bubbleChart.yAxis', 'Y-axis'),
      iconName: IconName.ARROW_TOP_ALT,
    }),
    axisFieldPicker({
      fieldName: 'selectedFieldNameRadius',
      label: t(
        i18nLocale,
        'tabview:bubbleChart.bubbleSizeLabel',
        'Bubble size'
      ),
      iconName: IconName.BUBBLE_CHART,
    }),
    toShowZeroSizeBubblesConfig(viewSettings, hasValueSource, i18nLocale),
    {
      ...getLayoutDropdownConfig(viewSettings, i18nLocale),
      dropdownSize: DropdownSize.S,
      maxDropdownHeight: DROPDOWN_MAX_HEIGHT,
    },
  ];
};
